import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { matterApi, partyApi, precedentApi, type MatterSummary, type Party, type Precedent } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

type IconName = Parameters<typeof Icon>[0]['name'];

type PaletteItem = {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon: IconName;
  to?: string;
  onSelect?: () => void | Promise<void>;
};

const PAGES: PaletteItem[] = [
  { id: 'page-dashboard',  to: '/',          label: 'Dashboard',   group: 'Pages', icon: 'grid' },
  { id: 'page-matters',    to: '/matters',   label: 'Matters',     group: 'Pages', icon: 'briefcase' },
  { id: 'page-tasks',      to: '/tasks',     label: 'Tasks',       group: 'Pages', icon: 'check-list' },
  { id: 'page-diary',      to: '/diary',     label: 'Diary',       group: 'Pages', icon: 'calendar' },
  { id: 'page-parties',    to: '/parties',   label: 'Parties',     group: 'Pages', icon: 'users' },
  { id: 'page-attorneys',  to: '/attorneys', label: 'Attorneys',   group: 'Pages', icon: 'balance-scale' },
  { id: 'page-invoices',   to: '/invoices',  label: 'Invoices',    group: 'Pages', icon: 'receipt' },
  { id: 'page-fee-book',   to: '/fee-book',  label: 'Fee Book',    group: 'Pages', icon: 'book-open' },
  { id: 'page-trust',      to: '/trust',     label: 'Trust ledger', group: 'Pages', icon: 'shield-check' },
  { id: 'page-documents',  to: '/documents', label: 'Document library', group: 'Pages', icon: 'document' },
  { id: 'page-precedents', to: '/precedents', label: 'Precedent library', group: 'Pages', icon: 'book-open' },
  { id: 'page-signing',    to: '/signing',   label: 'Signing',     group: 'Pages', icon: 'pen' },
  { id: 'page-settings',   to: '/settings',  label: 'Settings',    group: 'Pages', icon: 'cog' },
  { id: 'page-profile',    to: '/settings/profile', label: 'My profile', group: 'Pages', icon: 'user' },
];

const ACTIONS: PaletteItem[] = [
  { id: 'action-new-matter',   to: '/matters/new',            label: 'Create new matter',   group: 'Actions', icon: 'plus' },
  { id: 'action-invite-member',to: '/settings/members?invite=1', label: 'Invite a team member', group: 'Actions', icon: 'users' },
  { id: 'action-new-party',    to: '/parties?new=1',          label: 'Add new party',       group: 'Actions', icon: 'plus' },
  { id: 'action-new-precedent',to: '/precedents',             label: 'New precedent template', group: 'Actions', icon: 'plus' },
];

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const { memberships } = useAuth();
  const { selectTenant, activeTenantId } = useTenant();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [matters, setMatters] = useState<MatterSummary[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [precedents, setPrecedents] = useState<Precedent[]>([]);
  const [remoteLoaded, setRemoteLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Reset + load on open.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(0);
    setTimeout(() => inputRef.current?.focus(), 10);
    if (!remoteLoaded) {
      (async () => {
        try {
          const [m, p, pr] = await Promise.all([
            matterApi.list({}).catch(() => ({ matters: [] })),
            partyApi.list().catch(() => ({ parties: [] })),
            precedentApi.list().catch(() => ({ precedents: [] })),
          ]);
          setMatters(m.matters || []);
          setParties(p.parties || []);
          setPrecedents(pr.precedents || []);
          setRemoteLoaded(true);
        } catch { /* swallow — palette still works with static items */ }
      })();
    }
  }, [open, remoteLoaded]);

  // Build filtered item list.
  const items = useMemo<PaletteItem[]>(() => {
    const q = query.trim().toLowerCase();
    const staticMatches = [...PAGES, ...ACTIONS]
      .filter((i) => !q || i.label.toLowerCase().includes(q));

    const mattersMatches: PaletteItem[] = matters
      .filter((m) => {
        if (!q) return false;
        const hay = `${m.reference || ''} ${m.matter_name || ''} ${m.case_number || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6)
      .map((m) => ({
        id: `matter-${m.ROWID}`,
        label: m.matter_name,
        hint: m.reference,
        group: 'Matters',
        icon: 'briefcase' as IconName,
        to: `/matters/${m.ROWID}`,
      }));

    const partiesMatches: PaletteItem[] = parties
      .filter((p) => {
        if (!q) return false;
        const hay = `${p.display_name || ''} ${p.full_legal_name || ''} ${p.email || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6)
      .map((p) => ({
        id: `party-${p.ROWID}`,
        label: p.display_name,
        hint: p.email || p.full_legal_name,
        group: 'Parties',
        icon: 'users' as IconName,
        to: `/parties?id=${p.ROWID}`,
      }));

    const precedentMatches: PaletteItem[] = precedents
      .filter((p) => {
        if (!q) return false;
        const hay = `${p.title || ''} ${p.summary || ''} ${p.tags || ''} ${p.category || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6)
      .map((p) => ({
        id: `precedent-${p.ROWID}`,
        label: p.title,
        hint: p.category?.replace(/_/g, ' '),
        group: 'Precedents',
        icon: 'book-open' as IconName,
        to: `/precedents`,
      }));

    const tenantMatches: PaletteItem[] = memberships
      .filter((t) => {
        if (memberships.length < 2) return false; // don't clutter for solo
        if (!q) return false;
        return (t.tenant_name || '').toLowerCase().includes(q);
      })
      .slice(0, 4)
      .map((t) => ({
        id: `tenant-${t.tenant_id}`,
        label: t.tenant_name,
        hint: String(t.tenant_id) === activeTenantId ? 'Active chambers' : 'Switch to chambers',
        group: 'Chambers',
        icon: 'pin' as IconName,
        onSelect: () => {
          if (String(t.tenant_id) !== activeTenantId) selectTenant(t.tenant_id);
          navigate('/');
        },
      }));

    return [...staticMatches, ...mattersMatches, ...partiesMatches, ...precedentMatches, ...tenantMatches];
  }, [query, matters, parties, precedents, memberships, activeTenantId, selectTenant, navigate]);

  const runItem = useCallback((item: PaletteItem | undefined) => {
    if (!item) return;
    if (item.onSelect) {
      void item.onSelect();
    } else if (item.to) {
      navigate(item.to);
    }
    onClose();
  }, [navigate, onClose]);

  // Reset the highlighted row every time the query changes so Enter
  // always targets the first visible match instead of a stale index.
  useEffect(() => { setSelected(0); }, [query]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape')        { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown'){ e.preventDefault(); setSelected((s) => Math.min(s + 1, items.length - 1)); }
      else if (e.key === 'ArrowUp')  { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
      else if (e.key === 'Enter')    { e.preventDefault(); runItem(items[selected]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, items, selected, runItem, onClose]);

  // Keep the selected row in view as the user navigates.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${selected}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!open) return null;

  // Group for display while preserving a flat index for keyboard nav.
  const grouped: Record<string, Array<PaletteItem & { _idx: number }>> = {};
  items.forEach((item, idx) => {
    (grouped[item.group] ||= []).push({ ...item, _idx: idx });
  });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={shellStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <Icon name="search" size={16} style={{ color: 'var(--color-text-subtle)' }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, matters, parties, actions…"
            style={inputStyle}
          />
          <kbd style={kbdStyle}>ESC</kbd>
        </div>

        <div ref={listRef} style={listStyle}>
          {items.length === 0 ? (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--color-text-subtle)', fontSize: 13 }}>
              No matches for “{query}”.
            </div>
          ) : (
            Object.entries(grouped).map(([group, gItems]) => (
              <div key={group} style={{ marginBottom: 4 }}>
                <div style={groupLabelStyle}>{group}</div>
                {gItems.map((item) => {
                  const isSel = item._idx === selected;
                  return (
                    <button
                      key={item.id}
                      data-idx={item._idx}
                      onMouseEnter={() => setSelected(item._idx)}
                      onClick={() => runItem(item)}
                      style={{
                        ...rowStyle,
                        background: isSel ? 'var(--color-accent-soft)' : 'transparent',
                        color: isSel ? 'var(--color-accent)' : 'var(--color-text)',
                      }}
                    >
                      <Icon name={item.icon} size={15} style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                      {item.hint && <span style={hintStyle}>{item.hint}</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div style={footerStyle}>
          <span>↑ ↓ navigate · ↵ select · Esc close</span>
          <span style={{ opacity: 0.7 }}>⌘K to toggle</span>
        </div>
      </div>
    </div>
  );
}

// ---------- styles ----------

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 100,
  display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
  paddingTop: '12vh', paddingLeft: 16, paddingRight: 16,
  background: 'rgba(11, 18, 32, 0.55)',
  backdropFilter: 'blur(4px)',
};

const shellStyle: React.CSSProperties = {
  width: '100%', maxWidth: 620,
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  boxShadow: 'var(--shadow)',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '12px 14px',
  borderBottom: '1px solid var(--color-border)',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontSize: 14,
  color: 'var(--color-text)',
  padding: 0,
};

const kbdStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
  padding: '2px 6px',
  borderRadius: 6,
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-subtle)',
};

const listStyle: React.CSSProperties = {
  maxHeight: '50vh',
  overflowY: 'auto',
  padding: '6px 0',
};

const groupLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-text-subtle)',
  padding: '8px 16px 4px',
};

const rowStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 16px',
  border: 'none',
  fontSize: 13,
  textAlign: 'left',
  cursor: 'pointer',
  transition: 'background 80ms ease',
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--color-text-subtle)',
  maxWidth: '45%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 14px',
  borderTop: '1px solid var(--color-border)',
  background: 'var(--color-surface-2)',
  fontSize: 11,
  color: 'var(--color-text-subtle)',
};
